/**
 * Application-wide constants
 * 
 * IMPORTANT: Shared constants (statuses, types, routes) are imported from shared-constants.js
 * The source of truth is: influencers-be/shared-constants.js
 * 
 * To sync: Copy influencers-be/shared-constants.js to influencers/src/shared-constants.js
 */

import configData from './config.json';

// Import shared constants from backend (synced copy)
import {
  BUSINESS_CATEGORIES as BUSINESS_CATEGORIES_JS,
  VALID_CATEGORY_IDS as VALID_CATEGORY_IDS_JS,
  EVENT_STATUS as EVENT_STATUS_JS,
  USER_STATUS as USER_STATUS_JS,
  USER_TYPES as USER_TYPES_JS,
  APPLICATION_STATUS as APPLICATION_STATUS_JS,
  API_ROUTES as API_ROUTES_JS,
  FIELD_NAMES as FIELD_NAMES_JS,
  buildRoute as buildRouteJS,
  normalizeCategory as normalizeCategoryJS,
  getCategoryLabel as getCategoryLabelJS
} from './shared-constants';

// API Configuration (Frontend-specific)
export const API_URL = configData.apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:3020';
export const ENVIRONMENT = configData.environment || process.env.NODE_ENV || 'development';
export const VERSION = configData.version || '1.0.0';
export const SUPPORT_EMAIL = configData.supportEmail || 'support@adsfluencers.net';

// Supabase Configuration (Frontend-specific)
export const SUPABASE_URL = configData.supabase?.url || '';
export const SUPABASE_PUBLISHABLE_KEY = configData.supabase?.publishableKey || '';

// Oracle Bucket Configuration (Frontend-specific)
export const ORACLE_BUCKET_REGION = configData.oracleBucket?.region || '';
export const ORACLE_BUCKET_NAMESPACE = configData.oracleBucket?.namespace || '';
export const ORACLE_BUCKET_NAME = configData.oracleBucket?.bucketName || '';
export const MAX_PHOTO_SIZE_BYTES = configData.oracleBucket?.maxPhotoSize || 1 * 1024 * 1024; // 1MB default
export const ORACLE_BUCKET_PAR = configData.oracleBucket?.par || '';

// Application Constants (Frontend-specific)
export const MAX_RETRY_ATTEMPTS = configData.constants?.maxRetryAttempts || 5;
export const STEP_TRANSITION_DELAY_MS = configData.constants?.stepTransitionDelayMs || 500;
export const MIN_PHONE_NUMBER_LENGTH = configData.constants?.minPhoneNumberLength || 8;
export const MIN_PASSWORD_LENGTH = configData.constants?.minPasswordLength || 8;
export const MIN_PHOTOS_REQUIRED = configData.constants?.minPhotosRequired || 3;
export const MAX_PHOTOS_ALLOWED = configData.constants?.maxPhotosAllowed || 4;

// Image Compression Configuration
export const IMAGE_COMPRESSION = {
  maxWidth: configData.imageCompression?.maxWidth || 1920,
  maxHeight: configData.imageCompression?.maxHeight || 1920,
  maxSizeBytes: configData.imageCompression?.maxSizeBytes || 102400,
  qualityStart: configData.imageCompression?.qualityStart ?? 0.92,
  qualityStep: configData.imageCompression?.qualityStep ?? 0.12,
  qualityMin: configData.imageCompression?.qualityMin ?? 0.3,
  minWidth: configData.imageCompression?.minWidth || 640,
  minHeight: configData.imageCompression?.minHeight || 640,
  outputFormat: configData.imageCompression?.outputFormat || 'image/jpeg',
  enabled: configData.imageCompression?.enabled ?? true,
};

// ============================================================================
// SHARED CONSTANTS - Imported from shared-constants.js (Backend source of truth)
// ============================================================================

// Business/Event Categories
export interface Category {
  id: string;
  label: string;
}

export const BUSINESS_CATEGORIES: Category[] = BUSINESS_CATEGORIES_JS;
export const VALID_CATEGORY_IDS = VALID_CATEGORY_IDS_JS;
export const getCategoryLabel = getCategoryLabelJS;
export const normalizeCategory = normalizeCategoryJS;

// Event Status Values
export const EVENT_STATUS = EVENT_STATUS_JS;
export type EventStatusType = typeof EVENT_STATUS[keyof typeof EVENT_STATUS];
export const VALID_EVENT_STATUSES = Object.values(EVENT_STATUS);

// User Status Values
export const USER_STATUS = USER_STATUS_JS;
export type UserStatusType = typeof USER_STATUS[keyof typeof USER_STATUS];
export const VALID_USER_STATUSES = Object.values(USER_STATUS);

// User Type Values
export const USER_TYPES = USER_TYPES_JS;
export type UserType = typeof USER_TYPES[keyof typeof USER_TYPES];
export const VALID_USER_TYPES = Object.values(USER_TYPES);

// Application Status Values
export const APPLICATION_STATUS = APPLICATION_STATUS_JS;
export type ApplicationStatusType = typeof APPLICATION_STATUS[keyof typeof APPLICATION_STATUS];
export const VALID_APPLICATION_STATUSES = Object.values(APPLICATION_STATUS);

// API Routes
export const API_ROUTES = API_ROUTES_JS;

// Field Names
export const FIELD_NAMES = FIELD_NAMES_JS;

// Helper function to build route with parameters
export const buildRoute = (route: string, params: Record<string, string | number> = {}): string => {
  return buildRouteJS(route, params);
};

// ============================================================================
// FRONTEND-ONLY CONSTANTS
// ============================================================================

// Admin Tab Names (plural forms for UI)
export const ADMIN_TABS = {
  INFLUENCERS: 'influencers' as const,
  BUSINESSES: 'businesses' as const,
  SEARCH: 'search' as const
};

export type AdminTabType = typeof ADMIN_TABS[keyof typeof ADMIN_TABS];

// Frontend Routes (Navigation paths)
export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  FIRST_USER_LOGIN: '/first-user-login',
  RESET_PASSWORD: '/reset-password',
  ACCOUNT_BLOCKED: '/account-blocked',
  SUBSCRIPTION_EXPIRED: '/subscription-expired',
  
  // Signup routes
  INFLUENCER_SIGNUP_WIZARD: '/influencer-signup-wizard',
  BUSINESS_SIGNUP_WIZARD: '/business-signup-wizard',
  
  // Influencer routes
  INFLUENCER_HOME: '/influencer-home',
  EDIT_PROFILE: '/edit-profile',
  
  // Business routes
  BUSINESS_HOME: '/business-home',
  BUSINESS_PROFILE: '/business-profile',
  BUSINESS_EDIT_PROFILE: '/business-edit-profile',
  BUSINESS_INFLUENCERS: '/business-influencers',
  BUSINESS_INFLUENCER_DETAILS: '/business-influencer-details',
  BUSINESS_EVENT_CREATION: '/business-event-creation',
  
  // Shared routes
  EVENT_DETAILS: '/event/:eventId',
  
  // Admin routes
  ADMIN_USERS: '/admin-users',
  ADMIN_USER_PROFILE: '/admin-user-profile',
} as const;
